/**
 * ProviderHeader — persistent identity strip shown above the tabs on the
 * Provider Profile page (Feature E).
 *
 * Sub-stories implemented here:
 *   E1 — Name, short code, type, status badge, conditional action button.
 *   E2 — "Make Inactive" confirmation dialog (ACTIVE → INACTIVE).
 *   E3 — "Activate" / "Reactivate" confirmation dialog (PENDING/INACTIVE → ACTIVE).
 *
 * Write controls (status transitions) are gated to provider.can_manage —
 * global App.ADMIN or a per-provider "admin" access_role, resolved
 * server-side (see has_provider_admin_access in klyra_backend).
 */

import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { Button, ConfirmDialog, AlertMessage } from "@/components/reusable";
import { ProviderStatusBadge } from "@/components/providers/ProviderStatusBadge";
import { transitionProviderStatus } from "@/services/providersApi";
import { useTelemetry } from "@/hooks/useTelemetry";
import { PROVIDER_TYPE_LABELS } from "@/types/providers";
import type { ProviderOut, ProviderStatus, ProviderType } from "@/types/providers";

interface ProviderHeaderProps {
  provider: ProviderOut;
  /** Called after a successful status transition with the updated provider. */
  onStatusChanged: (updated: ProviderOut) => void;
}

// ── Action config per status ────────────────────────────────────────────────

interface StatusAction {
  label: string;
  confirmTitle: string;
  confirmDescription: string;
  confirmLabel: string;
  newStatus: "active" | "inactive";
  tone: "info" | "warning" | "danger";
  buttonVariant: "primary" | "danger" | "secondary";
}

function buildStatusAction(status: ProviderStatus, schemaCount: number): StatusAction | undefined {
  if (status === "pending") {
    const missingSchemas = schemaCount === 0;
    return {
      label: "Activate",
      confirmTitle: "Activate this provider?",
      confirmDescription: missingSchemas
        ? "Cannot activate yet: assign at least one schema first. Complete the schema assignments on the Overview tab, then try again."
        : "The provider will become active and available for data submissions.",
      confirmLabel: "Activate",
      newStatus: "active",
      tone: missingSchemas ? "warning" : "info",
      buttonVariant: "primary",
    };
  }
  if (status === "inactive") {
    const missingSchemas = schemaCount === 0;
    return {
      label: "Reactivate",
      confirmTitle: "Reactivate this provider?",
      confirmDescription: missingSchemas
        ? "Cannot reactivate yet: assign at least one schema first. Complete the schema assignments on the Overview tab, then try again."
        : "The provider will become active again and available for new data submissions.",
      confirmLabel: "Reactivate",
      newStatus: "active",
      tone: missingSchemas ? "warning" : "info",
      buttonVariant: "primary",
    };
  }
  if (status === "active") {
    return {
      label: "Make Inactive",
      confirmTitle: "Make this provider inactive?",
      confirmDescription:
        "The provider will stop accepting new submissions. Existing in-flight records will complete normally.",
      confirmLabel: "Make Inactive",
      newStatus: "inactive",
      tone: "warning",
      buttonVariant: "danger",
    };
  }
  return undefined;
}

// ── Component ───────────────────────────────────────────────────────────────

/**
 * Renders the provider identity strip — name, short code, type chip, status badge —
 * plus an admin-only status action button with a confirmation dialog.
 */
export function ProviderHeader({ provider, onStatusChanged }: ProviderHeaderProps) {
  const { track, trackError } = useTelemetry();
  const isAdmin = provider.can_manage;
  const schemaCount = provider.assigned_schemas.length;
  const missingSchemasForActivate =
    (provider.provider_status === "pending" || provider.provider_status === "inactive") &&
    schemaCount === 0;

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [transitionError, setTransitionError] = useState<string | null>(null);

  const action = useMemo(
    () => buildStatusAction(provider.provider_status as ProviderStatus, schemaCount),
    [provider.provider_status, schemaCount]
  );

  async function handleConfirmTransition() {
    if (!action || missingSchemasForActivate) return;
    setTransitioning(true);
    setTransitionError(null);
    try {
      const updated = await transitionProviderStatus(provider.provider_id, action.newStatus);
      track("provider_status_changed", {
        providerId: provider.provider_id,
        from: provider.provider_status,
        to: action.newStatus,
      });
      setConfirmOpen(false);
      onStatusChanged(updated);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Status transition failed. Please try again.";
      setTransitionError(message);
      trackError(err instanceof Error ? err : new Error(message), {
        context: "provider_status_transition",
      });
    } finally {
      setTransitioning(false);
    }
  }

  return (
    <>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4 rounded-xl border border-[var(--color-border-light)] bg-white px-6 py-5 shadow-sm">
        {/* Left: identity */}
        <div className="flex flex-col gap-2 min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight text-[var(--color-text-primary)] truncate">
              {provider.provider_name}
            </h1>
            <ProviderStatusBadge status={provider.provider_status as ProviderStatus} />
          </div>

          <div className="flex flex-wrap items-center gap-4 text-sm text-[var(--color-text-secondary)]">
            {/* Short code */}
            <span className="flex items-center gap-1.5">
              <span className="text-xs font-medium uppercase tracking-wider text-[var(--color-text-tertiary)]">
                Code
              </span>
              <code className="rounded bg-[var(--color-bg-secondary)] px-1.5 py-0.5 text-xs font-mono text-[var(--color-text-primary)]">
                {provider.provider_short_code}
              </code>
            </span>

            {/* Type chip */}
            <span className="flex items-center gap-1.5">
              <span className="text-xs font-medium uppercase tracking-wider text-[var(--color-text-tertiary)]">
                Type
              </span>
              <span
                className={cn(
                  "rounded-full border px-2.5 py-0.5 text-xs font-medium",
                  "bg-[var(--color-bg-secondary)] border-[var(--color-border)] text-[var(--color-text-secondary)]"
                )}
              >
                {PROVIDER_TYPE_LABELS[provider.provider_type as ProviderType] ??
                  provider.provider_type}
              </span>
            </span>

            {/* Schema count */}
            {schemaCount > 0 && (
              <span className="flex items-center gap-1.5">
                <span className="text-xs font-medium uppercase tracking-wider text-[var(--color-text-tertiary)]">
                  Schemas
                </span>
                <span className="text-[var(--color-text-secondary)]">{schemaCount}</span>
              </span>
            )}
          </div>

          {isAdmin && missingSchemasForActivate && (
            <p className="mt-1 text-xs text-amber-700">
              Assign at least one schema before activating this provider.
            </p>
          )}
        </div>

        {/* Right: admin action button */}
        {isAdmin && action && (
          <Button
            type="button"
            variant={action.buttonVariant}
            size="sm"
            onClick={() => setConfirmOpen(true)}
            disabled={transitioning || missingSchemasForActivate}
            title={
              missingSchemasForActivate ? "Assign at least one schema before activating" : undefined
            }
          >
            {action.label}
          </Button>
        )}
      </div>

      {/* Transition error (shown below header) */}
      {transitionError && (
        <div className="mb-4">
          <AlertMessage variant="error" message={transitionError} />
        </div>
      )}

      {/* E2 / E3 — status transition confirmation */}
      {action && (
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title={action.confirmTitle}
          description={action.confirmDescription}
          confirmLabel={transitioning ? "Saving…" : action.confirmLabel}
          cancelLabel="Cancel"
          tone={action.tone}
          onConfirm={handleConfirmTransition}
          confirmDisabled={transitioning || missingSchemasForActivate}
        />
      )}
    </>
  );
}
